/**
 * React Query hooks for Marketing Ops Dashboard
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import marketingService from '../../services/api/marketingService';

export const PIPELINE_KEY = ['marketing', 'pipeline'];
export const VISITOR_KEY = ['marketing', 'visitor'];
export const FEEDBACK_KEY = ['marketing', 'feedback'];
export const REPORTS_KEY = ['marketing', 'reports'];

/**
 * List generated reports (the 24-hour SLA queue).
 * Refetches every 60s so the sidebar badge stays live.
 */
export function useReports(params = {}) {
  return useQuery({
    queryKey: [...REPORTS_KEY, params],
    queryFn: () => marketingService.getReports(params),
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 60,
  });
}

/**
 * Single report detail (sections + register search + quote + application)
 */
export function useReport(reportId) {
  return useQuery({
    queryKey: [...REPORTS_KEY, 'detail', reportId],
    queryFn: () => marketingService.getReport(reportId),
    enabled: !!reportId,
    staleTime: 1000 * 15,
  });
}

/**
 * Re-run the report generator
 */
export function useRegenerateReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reportId) => marketingService.regenerateReport(reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REPORTS_KEY });
    },
  });
}

/**
 * Save report edits (sections / email overrides)
 */
export function useUpdateReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ reportId, ...updates }) => marketingService.updateReport(reportId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REPORTS_KEY });
    },
  });
}

/**
 * Edit the quote (writes back to the pricing record, quoteStatus: confirmed)
 */
export function useUpdateReportQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ reportId, ...quote }) => marketingService.updateReportQuote(reportId, quote),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REPORTS_KEY });
    },
  });
}

/**
 * Price a prospective quote scope without saving (live total in the editor)
 */
export function usePriceReportQuote() {
  return useMutation({
    mutationFn: ({ reportId, ...scope }) => marketingService.priceReportQuote(reportId, scope),
  });
}

/**
 * Send the report + quote email (magic link minted server-side)
 */
export function useSendReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ reportId, ...compose }) => marketingService.sendReport(reportId, compose),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REPORTS_KEY });
      queryClient.invalidateQueries({ queryKey: PIPELINE_KEY });
    },
  });
}

/**
 * Send a revised quote to a client who already has their report
 */
export function useSendQuoteRevision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ reportId, ...compose }) => marketingService.sendQuoteRevision(reportId, compose),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REPORTS_KEY });
      queryClient.invalidateQueries({ queryKey: PIPELINE_KEY });
    },
  });
}

/**
 * Class reference for the quote class picker. Cached for the session — the
 * class list is static. Only fetched while the picker is open.
 */
export function useNiceClasses(enabled = true) {
  return useQuery({
    queryKey: ['marketing', 'class-terms'],
    queryFn: () => marketingService.getClassTerms(),
    enabled,
    staleTime: Infinity,
  });
}

/**
 * AI-suggested goods/services for a class, already snapped to the IP
 * Australia picklist so suggestions keep the $250/class filing fee
 */
export function useSuggestGoodsServices() {
  return useMutation({
    mutationFn: ({ reportId, classNumber }) =>
      marketingService.suggestGoodsServices(reportId, classNumber),
  });
}

/**
 * Propose picklist replacements for non-picklist wording. Returns proposals;
 * nothing is written until the quote itself is saved.
 */
export function useFixPicklistTerms() {
  return useMutation({
    mutationFn: ({ reportId, classNumber }) =>
      marketingService.fixPicklistTerms(reportId, classNumber),
  });
}

/**
 * Country/pricing reference for the quote editor's country picker
 */
export function usePricingConfig(enabled = true) {
  return useQuery({
    queryKey: ['marketing', 'pricing-config'],
    queryFn: () => marketingService.getPricingConfig(),
    enabled,
    staleTime: Infinity,
  });
}

/**
 * IP Australia picklist terms for one class, optionally filtered by substring
 */
export function useNiceClassTerms(classNumber, q) {
  return useQuery({
    queryKey: ['marketing', 'class-terms', classNumber, q || ''],
    queryFn: () => marketingService.getClassTerms({ classNumber, q }),
    enabled: !!classNumber,
    staleTime: 1000 * 60 * 10,
  });
}

/**
 * Fetch visitor feedback (checkout prompt + email one-tap answers)
 */
export function useFeedback(params = {}) {
  return useQuery({
    queryKey: [...FEEDBACK_KEY, params],
    queryFn: () => marketingService.getFeedback(params),
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 60,
  });
}

/**
 * Fetch pipeline data (all visitors grouped by stage)
 */
export function usePipeline(params = {}) {
  return useQuery({
    queryKey: [...PIPELINE_KEY, params],
    queryFn: () => marketingService.getPipeline(params),
    staleTime: 1000 * 30, // 30s — pipeline changes frequently
    refetchInterval: 1000 * 60, // auto-refresh every 60s
  });
}

/**
 * Fetch single visitor detail
 */
export function useVisitorDetail(visitorId) {
  return useQuery({
    queryKey: [...VISITOR_KEY, visitorId],
    queryFn: () => marketingService.getVisitorDetail(visitorId),
    enabled: !!visitorId,
    staleTime: 1000 * 30,
  });
}

/**
 * People search (debounce in the component; queries of <2 chars disabled)
 */
export function usePeopleSearch(q) {
  return useQuery({
    queryKey: ['marketing', 'search', q],
    queryFn: () => marketingService.searchPeople(q),
    enabled: (q || '').trim().length >= 2,
    staleTime: 1000 * 30,
  });
}

/**
 * Bulk feedback-ask email
 */
export function useBulkFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (visitorIds) => marketingService.bulkFeedback(visitorIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PIPELINE_KEY });
      queryClient.invalidateQueries({ queryKey: FEEDBACK_KEY });
    },
  });
}

/**
 * Add note to visitor
 */
export function useAddNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ visitorId, content }) =>
      marketingService.addNote(visitorId, content),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [...VISITOR_KEY, variables.visitorId] });
      queryClient.invalidateQueries({ queryKey: PIPELINE_KEY });
    },
  });
}

/**
 * Send follow-up email. Accepts the full composer payload:
 * { visitorId, subject, bodyText, includeBlocks, templateOverride, customMessage }
 */
export function useSendFollowUp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ visitorId, ...compose }) =>
      marketingService.sendFollowUp(visitorId, compose),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [...VISITOR_KEY, variables.visitorId] });
      queryClient.invalidateQueries({ queryKey: PIPELINE_KEY });
    },
  });
}

/**
 * Set or clear a follow-up reminder (snooze)
 */
export function useSetReminder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ visitorId, days }) =>
      marketingService.setReminder(visitorId, days),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [...VISITOR_KEY, variables.visitorId] });
      queryClient.invalidateQueries({ queryKey: PIPELINE_KEY });
    },
  });
}

/**
 * Update visitor status
 */
export function useUpdateStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ visitorId, status }) =>
      marketingService.updateStatus(visitorId, status),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [...VISITOR_KEY, variables.visitorId] });
      queryClient.invalidateQueries({ queryKey: PIPELINE_KEY });
    },
  });
}
